import { useState, useEffect } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import {
    FormInput,
    FormDropDownListFilter,
    FormNumericTextBox,
    FormDatePicker,
    FormSwitch,
    FormTextArea,
    FormAddressLookup,
} from "../FormComponents";
import { getGoNoGo, updateGoNoGo, deleteGoNoGo, GoNoGoItem } from "../../../data/goNoGoData";
import { searchAddresses, getFullAddress } from "../../../helpers/addressLookup";
import { getClientsDropdown } from "../../../data/clientData";
import { getAddressesForClientDropdown, getClientsForClientsDropdown } from "../../../data/accountsData";
import { dropDown } from "../../../data/interfaces";
import Notes from '../../../pages/Notes/Notes';
import "../../../pages/GoNoGo/GoNoGo.css";

const EditGoNoGoForm = (props: any) => {
    const { goNoGoId, onClose, lockEdit } = props;
    const [item, setItem] = useState<GoNoGoItem | null>(null);
    const [addresses, setAddresses] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);

    // Mode: 'existing' or 'manual' — determined from loaded item
    const [mode, setMode] = useState<'existing' | 'manual'>('manual');

    // Existing mode state
    const [clientData, setClientData] = useState<dropDown[]>([]);
    const [siteData, setSiteData] = useState<dropDown[]>([]);
    const [contactData, setContactData] = useState<dropDown[]>([]);
    const [selectedClient, setSelectedClient] = useState<dropDown | null>(null);
    const [selectedSite, setSelectedSite] = useState<dropDown | null>(null);
    const [selectedContact, setSelectedContact] = useState<dropDown | null>(null);

    useEffect(() => {
        getClientsDropdown('').then(setClientData).catch(console.error);
    }, []);

    useEffect(() => {
        getGoNoGo(goNoGoId).then((loaded) => {
            setItem(loaded);
            // Determine mode from loaded item
            if (loaded.clientId) {
                setMode('existing');
                if (loaded.clientId && loaded.clientName) {
                    setSelectedClient({ value: loaded.clientId, text: loaded.clientName });
                }
                if (loaded.siteId && loaded.siteName) {
                    setSelectedSite({ value: loaded.siteId, text: loaded.siteName });
                }
            } else {
                setMode('manual');
            }
        }).catch(console.error);
    }, [goNoGoId]);

    useEffect(() => {
        if (selectedClient && Number(selectedClient.value) > 0) {
            getAddressesForClientDropdown(Number(selectedClient.value)).then((data) => {
                setSiteData(data);
                if (item?.siteId) {
                    const match = data.find(d => Number(d.value) === item.siteId);
                    if (match) setSelectedSite(match);
                    else if (data.length === 1) setSelectedSite(data[0]);
                } else if (data.length === 1) {
                    setSelectedSite(data[0]);
                }
            }).catch(console.error);
            getClientsForClientsDropdown(Number(selectedClient.value)).then((data) => {
                setContactData(data);
                if (item?.contactId) {
                    const match = data.find(d => Number(d.value) === item.contactId);
                    if (match) setSelectedContact(match);
                    else if (data.length === 1) setSelectedContact(data[0]);
                } else if (data.length === 1) {
                    setSelectedContact(data[0]);
                }
            }).catch(console.error);
        } else {
            setSiteData([]);
            setContactData([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClient]);

    const searchClientDropdown = async (term: string) => {
        const data = await getClientsDropdown(term);
        setClientData(data);
    };

    const handleSubmit = async (values: any) => {
        setSaving(true);
        try {
            await updateGoNoGo(goNoGoId, {
                client: mode === 'manual' ? (values.client || null) : (selectedClient?.text || null),
                sector: values.sector || null,
                value: values.value ?? null,
                location: values.location || null,
                reference: values.reference || null,
                dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : null,
                qualityPriceWeight: values.qualityPriceWeight || null,
                capacityToBid: values.capacityToBid ?? null,
                existingRelationship: values.existingRelationship ?? null,
                link: values.link || null,
                dateOnsite: values.dateOnsite ? new Date(values.dateOnsite).toISOString() : null,
                duration: values.duration || null,
                capacityToDeliver: values.capacityToDeliver ?? null,
                description: values.description || null,
                go: mode === 'existing' ? (values.go ?? false) : false,
                clientId: mode === 'existing' ? (selectedClient ? Number(selectedClient.value) : null) : null,
                siteId: mode === 'existing' ? (selectedSite ? Number(selectedSite.value) : null) : null,
                contactId: mode === 'existing' ? (selectedContact ? Number(selectedContact.value) : null) : null,
            });
            onClose();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await deleteGoNoGo(goNoGoId);
            onClose();
        } catch (e) {
            console.error(e);
        }
    };

    const handleAddressSelect = (addressId: string, formRenderProps: any) => {
        getFullAddress(addressId).then((full: any) => {
            if (full) {
                formRenderProps.onChange("location", { value: full.suggestion || full.line1 || "" });
            }
        });
    };

    if (!item) return <div style={{ padding: 16 }}>Loading...</div>;

    const canSetGo = mode === 'existing' && !!selectedClient && !!selectedSite;

    const initialValues = {
        reference: item.reference || "",
        client: item.client || "",
        sector: item.sector || "",
        value: item.value ?? null,
        location: item.location || "",
        dueDate: item.dueDate ? new Date(item.dueDate) : null,
        qualityPriceWeight: item.qualityPriceWeight || "",
        capacityToBid: item.capacityToBid ?? false,
        existingRelationship: item.existingRelationship ?? false,
        link: item.link || "",
        dateOnsite: item.dateOnsite ? new Date(item.dateOnsite) : null,
        duration: item.duration || "",
        capacityToDeliver: item.capacityToDeliver ?? false,
        description: item.description || "",
        go: item.go ?? false,
    };

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "0 16px 8px", alignItems: "center" }}>
                {item.enquiryId && (
                    <span style={{ fontSize: "0.85em", color: "#27ae60", fontWeight: 600 }}>
                        ✓ Enquiry #{item.enquiryId} created
                    </span>
                )}
                <div style={{ flex: 1 }} />
                {!lockEdit && (
                    <Button themeColor="error" fillMode="flat" onClick={() => setDeleteConfirmVisible(true)}>
                        Delete
                    </Button>
                )}
            </div>
            {item.link && item.link.startsWith("https://") && (
                <div style={{ padding: "0 16px 8px" }}>
                    <a href={item.link} target="_blank" rel="noopener noreferrer">{item.link}</a>
                </div>
            )}
            <TabStrip selected={selectedTab} onSelect={(e) => setSelectedTab(e.selected)}>
                <TabStripTab title="Details">
                    {/* Mode toggle */}
                    {!lockEdit && (
                        <div style={{ padding: "0 0 12px", display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: "0.9em", fontWeight: 600 }}>Mode:</span>
                            <button
                                type="button"
                                onClick={() => setMode('existing')}
                                style={{
                                    padding: "4px 12px", borderRadius: 4, border: "1px solid #ccc", cursor: "pointer",
                                    background: mode === 'existing' ? "#3498db" : "#fff",
                                    color: mode === 'existing' ? "#fff" : "#333",
                                    fontWeight: mode === 'existing' ? 600 : 400,
                                }}
                            >
                                Existing Client/Site
                            </button>
                            <button
                                type="button"
                                onClick={() => setMode('manual')}
                                style={{
                                    padding: "4px 12px", borderRadius: 4, border: "1px solid #ccc", cursor: "pointer",
                                    background: mode === 'manual' ? "#3498db" : "#fff",
                                    color: mode === 'manual' ? "#fff" : "#333",
                                    fontWeight: mode === 'manual' ? 600 : 400,
                                }}
                            >
                                Manual Entry
                            </button>
                        </div>
                    )}
                    <Form
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        render={(formRenderProps) => (
                            <FormElement style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="reference" label="Reference" component={FormInput} disabled={lockEdit} />
                                    </div>
                                </div>

                                {mode === 'existing' ? (
                                    <>
                                        <div className="form-row">
                                            <div className="form-field" style={{ flex: 1 }}>
                                                <FormDropDownListFilter
                                                    data={clientData}
                                                    originalData={clientData}
                                                    value={selectedClient}
                                                    name="clientDropdown"
                                                    label="Client"
                                                    setData={setClientData}
                                                    searchDropdown={searchClientDropdown}
                                                    disabled={lockEdit}
                                                    onChange={(e: any) => {
                                                        setSelectedClient(e.value);
                                                        setSelectedSite(null);
                                                        setSelectedContact(null);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-field" style={{ flex: 1 }}>
                                                <FormDropDownListFilter
                                                    data={siteData}
                                                    originalData={siteData}
                                                    value={selectedSite}
                                                    name="siteDropdown"
                                                    label="Site"
                                                    setData={setSiteData}
                                                    searchDropdown={() => {}}
                                                    disabled={lockEdit || !selectedClient}
                                                    onChange={(e: any) => setSelectedSite(e.value)}
                                                />
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-field" style={{ flex: 1 }}>
                                                <FormDropDownListFilter
                                                    data={contactData}
                                                    originalData={contactData}
                                                    value={selectedContact}
                                                    name="contactDropdown"
                                                    label="Contact"
                                                    setData={setContactData}
                                                    searchDropdown={() => {}}
                                                    disabled={lockEdit || !selectedClient}
                                                    onChange={(e: any) => setSelectedContact(e.value)}
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="form-row">
                                            <div className="form-field">
                                                <Field name="client" label="Client (manual)" component={FormInput} disabled={lockEdit} />
                                            </div>
                                            <div className="form-field">
                                                {lockEdit ? (
                                                    <Field name="location" label="Location" component={FormInput} disabled />
                                                ) : (
                                                    <Field
                                                        name="addressLookup"
                                                        label="Location"
                                                        component={FormAddressLookup}
                                                        data={addresses}
                                                        searchDropdown={(term: string) => searchAddresses(term, setAddresses)}
                                                        onChange={(e: any) => {
                                                            if (e.value?.id) handleAddressSelect(e.value.id, formRenderProps);
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="sector" label="Sector" component={FormInput} disabled={lockEdit} />
                                    </div>
                                    <div className="form-field">
                                        <Field name="value" label="Value (£)" component={FormNumericTextBox} format="n2" disabled={lockEdit} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="dueDate" label="Due Date" component={FormDatePicker} disabled={lockEdit} />
                                    </div>
                                    <div className="form-field">
                                        <Field name="qualityPriceWeight" label="Quality/Price Weight" component={FormInput} disabled={lockEdit} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="dateOnsite" label="Date Onsite" component={FormDatePicker} disabled={lockEdit} />
                                    </div>
                                    <div className="form-field">
                                        <Field name="link" label="Link" component={FormInput} disabled={lockEdit} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="duration" label="Duration" component={FormInput} disabled={lockEdit} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="capacityToBid" label="Capacity to Bid?" component={FormSwitch} disabled={lockEdit} />
                                    </div>
                                    <div className="form-field">
                                        <Field name="existingRelationship" label="Existing Relationship?" component={FormSwitch} disabled={lockEdit} />
                                    </div>
                                    <div className="form-field">
                                        <Field name="capacityToDeliver" label="Capacity to Deliver?" component={FormSwitch} disabled={lockEdit} />
                                    </div>
                                </div>
                                <div className="form-row" style={{ flex: 1 }}>
                                    <div className="form-field">
                                        <Field name="description" label="Description" component={FormTextArea} rows={4} disabled={lockEdit} />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="go" label="Go?" component={FormSwitch} disabled={lockEdit || !canSetGo} />
                                        {!canSetGo && !lockEdit && (
                                            <span style={{ fontSize: "0.75em", color: "#e74c3c" }}>
                                                Requires existing client &amp; site
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {!lockEdit && (
                                    <div className="form-actions">
                                        <Button
                                            type="submit"
                                            themeColor="primary"
                                            disabled={saving || !formRenderProps.allowSubmit}
                                        >
                                            {saving ? "Saving..." : "Save"}
                                        </Button>
                                    </div>
                                )}
                            </FormElement>
                        )}
                    />
                </TabStripTab>
                <TabStripTab title="Comments">
                    <Notes
                        lockEdit={lockEdit}
                        sourceEntity={{
                            goNoGoId: goNoGoId,
                            enquiryId: null,
                            quoteId: null,
                            jobId: null,
                            vehicleId: null,
                            toolId: null,
                            serviceReminderId: null,
                            invoiceId: null,
                        }}
                    />
                </TabStripTab>
            </TabStrip>

            {deleteConfirmVisible && (
                <Dialog title="Confirm Delete" onClose={() => setDeleteConfirmVisible(false)}>
                    <p>Are you sure you want to delete this Go/No-Go record?</p>
                    <DialogActionsBar>
                        <Button onClick={() => setDeleteConfirmVisible(false)}>Cancel</Button>
                        <Button themeColor="error" onClick={handleDelete}>Delete</Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
};

export default EditGoNoGoForm;
